import { useEffect, useRef, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Download, PackagePlus } from "lucide-react";

const empty = { brand: "", flavor: "", ufi: "", batch_number: "", dluo: "", nicotine: "", pg_vg_ratio: "" };

export default function Liquids() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [flaconFor, setFlaconFor] = useState(null); // parent liquid for "nouveau flacon"
  const [flaconForm, setFlaconForm] = useState({ batch_number: "", dluo: "" });

  const load = () => api.get("/liquids").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const dluo = normalizeDluo(form.dluo);
    if (form.dluo && !dluo) {
      toast.error("Format DLUO invalide (attendu MM/AA)");
      return;
    }
    const payload = {
      brand: form.brand.trim(),
      flavor: form.flavor.trim(),
      ufi: form.ufi.trim(),
      batch_number: form.batch_number.trim(),
      dluo,
      nicotine: form.nicotine === "" ? null : Number(form.nicotine),
      pg_vg_ratio: form.pg_vg_ratio.trim(),
    };
    if (!payload.brand || !payload.flavor) {
      toast.error("Marque et saveur sont requis");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/liquids/${editingId}`, payload);
        toast.success("Liquide mis à jour");
      } else {
        await api.post("/liquids", payload);
        toast.success("Liquide ajouté");
      }
      setOpen(false); setForm(empty); setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce liquide ?")) return;
    try {
      await api.delete(`/liquids/${id}`);
      toast.success("Supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const edit = (item) => {
    setEditingId(item.id);
    setForm({
      brand: item.brand || "", flavor: item.flavor || "", ufi: item.ufi || "",
      batch_number: item.batch_number || "",
      dluo: item.dluo || "",
      nicotine: item.nicotine ?? "", pg_vg_ratio: item.pg_vg_ratio || "",
    });
    setOpen(true);
  };

  const openFlaconDialog = (item) => {
    setFlaconFor(item);
    setFlaconForm({ batch_number: "", dluo: "" });
  };

  const saveFlacon = async () => {
    if (!flaconFor) return;
    const dluo = normalizeDluo(flaconForm.dluo);
    if (flaconForm.dluo && !dluo) {
      toast.error("Format DLUO invalide (attendu MM/AA)");
      return;
    }
    const payload = {
      brand: flaconFor.brand,
      flavor: flaconFor.flavor,
      ufi: (flaconFor.ufi || "").trim(),
      batch_number: flaconForm.batch_number.trim(),
      dluo,
      nicotine: flaconFor.nicotine ?? null,
      pg_vg_ratio: flaconFor.pg_vg_ratio || "",
    };
    try {
      await api.post("/liquids", payload);
      toast.success(`Nouveau flacon de ${flaconFor.brand} — ${flaconFor.flavor} enregistré`);
      setFlaconFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <DashboardLayout title="Bibliothèque de liquides">
      <div className="mb-6 flex items-center justify-between">
        <div className="text-sm text-zinc-400">
          {items.length} parfum{items.length > 1 ? "s" : ""} enregistré{items.length > 1 ? "s" : ""}
        </div>
        <div className="flex gap-2">
          <CsvImportButton onImported={load} />
          <Button data-testid="liquid-template-btn" onClick={downloadTemplate} variant="outline"
            className="rounded-none border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white">
            <Download className="w-4 h-4 mr-2" /> Modèle CSV
          </Button>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty); setEditingId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="liquid-add-btn" className="bg-red-500 hover:bg-red-600 rounded-none">
              <Plus className="w-4 h-4 mr-2" /> Ajouter un liquide
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 rounded-none max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {editingId ? "Modifier le liquide" : "Nouveau liquide"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <F label="Marque"><Input data-testid="liquid-brand" className="rounded-none bg-zinc-900 border-zinc-800" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></F>
              <F label="Saveur / Parfum"><Input data-testid="liquid-flavor" className="rounded-none bg-zinc-900 border-zinc-800" value={form.flavor} onChange={(e) => setForm({ ...form, flavor: e.target.value })} /></F>
              <F label="Numéro UFI"><Input data-testid="liquid-ufi" className="rounded-none bg-zinc-900 border-zinc-800 font-mono" value={form.ufi} onChange={(e) => setForm({ ...form, ufi: e.target.value })} placeholder="XXXX-XXXX-XXXX-XXXX" /></F>
              <F label="Numéro de lot"><Input data-testid="liquid-batch" className="rounded-none bg-zinc-900 border-zinc-800 font-mono" value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} placeholder="Ex. LOT-2026-A123" /></F>
              <div className="grid grid-cols-2 gap-4">
                <F label="DLUO (MM/AA)">
                  <Input data-testid="liquid-dluo" className="rounded-none bg-zinc-900 border-zinc-800 font-mono"
                    value={form.dluo} onChange={(e) => setForm({ ...form, dluo: formatDluoInput(e.target.value) })}
                    placeholder="03/28" maxLength={5} />
                </F>
                <F label="Nicotine (mg/ml)"><Input data-testid="liquid-nic" type="number" step="0.1" min="0" className="rounded-none bg-zinc-900 border-zinc-800" value={form.nicotine} onChange={(e) => setForm({ ...form, nicotine: e.target.value })} placeholder="0" /></F>
              </div>
              <F label="Ratio PG/VG"><Input data-testid="liquid-pgvg" className="rounded-none bg-zinc-900 border-zinc-800" value={form.pg_vg_ratio} onChange={(e) => setForm({ ...form, pg_vg_ratio: e.target.value })} placeholder="50/50" /></F>
              <Button data-testid="liquid-save-btn" onClick={save} className="w-full bg-red-500 hover:bg-red-600 rounded-none">
                {editingId ? "Enregistrer" : "Créer"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="border border-zinc-800">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900 border-b border-zinc-800">
            <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <th className="px-4 py-3">Marque</th>
              <th className="px-4 py-3">Saveur</th>
              <th className="px-4 py-3">UFI</th>
              <th className="px-4 py-3">N° Lot</th>
              <th className="px-4 py-3">DLUO</th>
              <th className="px-4 py-3">Nicotine</th>
              <th className="px-4 py-3">PG/VG</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-500 text-sm">Aucun liquide. Cliquez sur "Ajouter un liquide" pour commencer.</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} data-testid={`liquid-row-${it.id}`} className="border-b border-zinc-800 hover:bg-zinc-900/60">
                <td className="px-4 py-3 font-semibold text-white">{it.brand}</td>
                <td className="px-4 py-3 text-zinc-300">{it.flavor}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{it.ufi || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{it.batch_number || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{it.dluo || "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{it.nicotine ?? "—"}</td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-400">{it.pg_vg_ratio || "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`liquid-flacon-${it.id}`} onClick={() => openFlaconDialog(it)} title="Nouveau flacon (même parfum)"
                    className="text-zinc-400 hover:text-red-500 p-2"><PackagePlus className="w-4 h-4" /></button>
                  <button data-testid={`liquid-edit-${it.id}`} onClick={() => edit(it)} className="text-zinc-400 hover:text-white p-2" title="Modifier"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`liquid-delete-${it.id}`} onClick={() => del(it.id)} className="text-zinc-400 hover:text-red-500 p-2" title="Supprimer"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Nouveau flacon dialog */}
      <Dialog open={!!flaconFor} onOpenChange={(o) => { if (!o) setFlaconFor(null); }}>
        <DialogContent className="bg-zinc-950 border-zinc-800 rounded-none max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Nouveau flacon</DialogTitle>
          </DialogHeader>
          {flaconFor && (
            <div className="space-y-4">
              <div className="border border-zinc-800 bg-zinc-900 p-3">
                <div className="font-display text-lg font-bold text-white">{flaconFor.brand}</div>
                <div className="text-sm text-zinc-400">{flaconFor.flavor}</div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                  UFI · <span className="text-white normal-case tracking-normal" data-testid="flacon-ufi-readonly">{flaconFor.ufi || "—"}</span>
                </div>
                {(flaconFor.nicotine != null || flaconFor.pg_vg_ratio) && (
                  <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                    {flaconFor.nicotine != null && <>Nicotine {flaconFor.nicotine} mg/ml · </>}
                    {flaconFor.pg_vg_ratio && <>PG/VG {flaconFor.pg_vg_ratio}</>}
                  </div>
                )}
              </div>
              <div className="text-xs text-zinc-500">Le numéro UFI est propre à la référence produit et ne change pas d'un flacon à l'autre. Saisissez uniquement les infos du nouveau flacon reçu :</div>
              <F label="Numéro de lot">
                <Input data-testid="flacon-batch" autoFocus className="rounded-none bg-zinc-900 border-zinc-800 font-mono"
                  value={flaconForm.batch_number} onChange={(e) => setFlaconForm({ ...flaconForm, batch_number: e.target.value })}
                  placeholder="Ex. LOT-2026-A123" />
              </F>
              <F label="DLUO (MM/AA)">
                <Input data-testid="flacon-dluo" className="rounded-none bg-zinc-900 border-zinc-800 font-mono"
                  value={flaconForm.dluo} onChange={(e) => setFlaconForm({ ...flaconForm, dluo: formatDluoInput(e.target.value) })}
                  placeholder="03/28" maxLength={5} />
              </F>
              <Button data-testid="flacon-save" onClick={saveFlacon} className="w-full bg-red-500 hover:bg-red-600 rounded-none">
                Enregistrer le nouveau flacon
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">{label}</label>
      {children}
    </div>
  );
}

function formatDluoInput(raw) {
  // Keep only digits and '/'; auto-insert '/' after 2 digits
  const digits = String(raw || "").replace(/[^0-9]/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return digits.slice(0, 2) + "/" + digits.slice(2);
}

function normalizeDluo(raw) {
  const s = String(raw || "").trim();
  const m = s.match(/^(\d{1,2})[/\-.](\d{2,4})$/);
  if (!m) return "";
  const mm = parseInt(m[1], 10);
  let yy = parseInt(m[2], 10);
  if (yy >= 100) yy = yy % 100;
  if (mm < 1 || mm > 12) return "";
  return `${String(mm).padStart(2, "0")}/${String(yy).padStart(2, "0")}`;
}

function formatDuration(total_months) {
  const t = Number(total_months) || 0;
  const y = Math.floor(t / 12);
  const m = t % 12;
  if (y === 0) return `${m} mois`;
  if (m === 0) return `${y} an${y > 1 ? "s" : ""}`;
  return `${y} an${y > 1 ? "s" : ""} ${m} mois`;
}

const CSV_TEMPLATE = `marque,saveur,ufi,lot,dluo,nicotine,pg_vg
Alfaliquid,Fraise Menthe,AAAA-BBBB-CCCC-DDDD,LOT-2026-A1,03/28,3,50/50
Le French Liquide,Vanille Custard,XXXX-YYYY-ZZZZ-1234,LOT-2026-B2,09/27,6,30/70
`;

function downloadTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "modele_liquides.csv"; a.click();
  URL.revokeObjectURL(url);
}

function CsvImportButton({ onImported }) {
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const csv_text = await f.text();
      const { data } = await api.post("/liquids/import-csv", { csv_text });
      toast.success(`${data.inserted} liquide(s) importé(s)`);
      if (data.errors?.length) {
        toast.warning(`${data.errors.length} ligne(s) ignorée(s)`);
      }
      onImported();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="liquid-import-btn" variant="outline"
          className="rounded-none border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white">
          <Upload className="w-4 h-4 mr-2" /> Importer CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Importer une gamme de liquides</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-zinc-400">
            Formats acceptés : CSV avec virgule ou point-virgule.
            Colonnes attendues : <span className="font-mono text-zinc-200">marque, saveur, ufi, lot, dluo, nicotine, pg_vg</span>.
            Le champ <span className="font-mono text-zinc-200">dluo</span> est au format <span className="font-mono">MM/AA</span>.
            Les colonnes en trop sont ignorées.
          </div>
          <div className="border border-zinc-800 bg-zinc-900 p-3 font-mono text-xs text-zinc-400 overflow-x-auto whitespace-pre">
            {CSV_TEMPLATE}
          </div>
          <label className="block border border-dashed border-zinc-700 hover:border-red-500/60 p-6 cursor-pointer transition-colors text-center" data-testid="liquid-import-drop">
            <Upload className="w-6 h-6 mx-auto text-zinc-500" />
            <div className="text-sm text-zinc-300 mt-2">{importing ? "Import en cours…" : "Cliquez pour sélectionner un fichier CSV"}</div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" data-testid="liquid-import-input" />
          </label>
          <Button onClick={downloadTemplate} variant="outline" className="w-full rounded-none border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:text-white">
            <Download className="w-4 h-4 mr-2" /> Télécharger le modèle
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
